import { useEffect, useRef, useState } from "react";
import MoreIcon from "../../../../../media/icons/assets_light/outline-action-menu-2-active.svg";
import clearIcon from "../../../../../media/icons/assets_light/outline-action-close-active.svg";
import ExpanseIcon from "../../../../../media/icons/assets_light/outline-menu-expenses-active.svg";
import BackIcon from "../../../../../media/icons/assets_light/move-back-2.svg";
import eyeIcon from "../../../../../media/icons/assets_light/outline-action-view-active.svg";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { InputValidationCheck } from "../../../../ValidationCheck";
import { useDispatch } from "react-redux";
import { useTaskContext } from "../../../../../context/TaskContext";

export default function CostTrackingPanel() {
  const dispatch = useDispatch();
  const { OnUpdateTask, isObserver, user } = useTaskContext()
  const { active_task } = useSelector(state => state.task);
  const [all_breakdown, set_all_breakdown] = useState(active_task?.cost_breakdown?.map(({ actual_cost, cost_title, forecasted_cost }) => ({ actual_cost, cost_title, forecasted_cost })) || []);
  const [mainCostBreakdown, setMainCostBreakdown] = useState({ cost_title: '', forecasted_cost: '', actual_cost: '' });
  const input_ref = useRef(null);

  useEffect(() => {
    set_all_breakdown(active_task?.cost_breakdown)
  }, [active_task?.cost_breakdown])

  // Helper functions
  const [openMenu, setOpenMenu] = useState(null);
  const removeOptionRef = useRef(null);

  // Close popup on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (removeOptionRef.current && !removeOptionRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);

    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const [showDetails, setShowDetails] = useState(false);

  const [all_breakdown_cost, set_all_breakdown_cost] = useState(active_task?.cost_breakdown?.map(({ actual_cost, cost_title, forecasted_cost }) => ({ actual_cost, cost_title, forecasted_cost })) || []);

  useEffect(() => {
    set_all_breakdown_cost(active_task?.cost_breakdown?.map(({ actual_cost, cost_title, forecasted_cost }) => ({ actual_cost, cost_title, forecasted_cost })) || [])
  }, [active_task?.cost_breakdown])

  const totalCosts = (all_breakdown_cost || []).reduce((totals, item) => {
    totals.forecasted_cost += Number(item?.forecasted_cost) || 0;
    totals.actual_cost += Number(item?.actual_cost) || 0;
    totals.cost_variance = Number(item?.forecasted_cost - item?.actual_cost) || 0;
    return totals;
  }, { forecasted_cost: 0, actual_cost: 0, cost_variance: 0 });


  const handleChange = (e) => {
    if (isObserver()) return;
    const { name, value, type } = e.target;

    // Allow empty values for clearing
    if (value === '') {
      return setMainCostBreakdown(prev => ({ ...prev, [name]: value }));
    }

    // Handle number fields with non-negative validation
    if (type === 'number') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue >= 0) {
        return setMainCostBreakdown(prev => ({ ...prev, [name]: value }));
      } else {
        console.warn('Negative values are not allowed.');
        return;
      }
    }

    // Handle text fields with external validation
    if (InputValidationCheck(value)) {
      console.warn('Invalid input');
      return;
    }

    setMainCostBreakdown(prev => ({ ...prev, [name]: value }));
  };

  const handleAddRow = () => {
    if (isObserver()) return;
    if (mainCostBreakdown.cost_title?.trim() !== '' && (mainCostBreakdown?.forecasted_cost?.trim() !== '' || mainCostBreakdown?.actual_cost?.trim() !== '')) {
      let forecasted_cost = Number(totalCosts?.forecasted_cost) + Number(mainCostBreakdown?.forecasted_cost);
      let actual_cost = Number(totalCosts?.actual_cost) + Number(mainCostBreakdown?.actual_cost);
      OnUpdateTask({
        cost_breakdown: [
          ...(all_breakdown?.map(({ cost_title, forecasted_cost, actual_cost }) => ({ cost_title, forecasted_cost, actual_cost })) || []),
          mainCostBreakdown,
        ],
        forecasted_cost: forecasted_cost,
        actual_cost: actual_cost,
        cost_variance: Number(forecasted_cost - actual_cost),
        save_type: 'cost'
      })
      setMainCostBreakdown({ cost_title: '', forecasted_cost: '', actual_cost: '' })
      input_ref.current.focus();
    }
  };

  const handleRemoveClick = (cost, index) => {
    if (isObserver()) return;
    setOpenMenu(null)
    let break_down = all_breakdown.map(({ actual_cost, cost_title, forecasted_cost }) => ({ actual_cost, cost_title, forecasted_cost }))
    const updatedCostBreakdown = [...break_down];
    updatedCostBreakdown.splice(index, 1);

    let forecasted_cost = Number(totalCosts?.forecasted_cost) - Number(cost?.forecasted_cost);
    let actual_cost = Number(totalCosts?.actual_cost) - Number(cost?.actual_cost);
    OnUpdateTask({
      cost_breakdown: updatedCostBreakdown,
      forecasted_cost: forecasted_cost,
      actual_cost: actual_cost,
      cost_variance: Number(forecasted_cost - actual_cost),
      save_type: 'cost'
    })
  };

  return (
    <div className="dark:bg-surface-dark-2 bg-white min-h-full">
      {/* First View - Summary Card */}
      {!showDetails && (
        <div className="">
          {/* Header */}
          <div className="flex justify-between items-center py-3">
            <h2 className="text-sm font-semibold text-gray-800 dark:text-white">
              Total cost tracked
            </h2>
            <button
              onClick={() => setShowDetails(true)}
              disabled={isObserver()}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-medium transition-all active:scale-95
                ${isObserver()
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-teal-600 text-white shadow-sm shadow-teal-500/25 active:bg-teal-600"}`}
            >
              <span className="text-sm leading-none">+</span>
              <span>Add cost</span>
            </button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            {/* Forecasted Card */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-3 border border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-1.5 mb-1.5">
                <div className="w-6 h-6 rounded-lg bg-blue-500 flex items-center justify-center">
                  <span className="text-white text-xs font-bold">FC</span>
                </div>
                <span className="text-xs font-medium text-blue-600 dark:text-blue-400">Forecasted</span>
              </div>
              <p className="text-sm font-bold text-gray-800 dark:text-white">
                {totalCosts?.forecasted_cost ? `CAD ${totalCosts?.forecasted_cost.toFixed(2)}` : "CAD 0.00"}
              </p>
            </div>

            {/* Actual Card */}
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 rounded-xl p-3 border border-emerald-200 dark:border-emerald-800">
              <div className="flex items-center gap-1.5 mb-1.5">
                <div className="w-6 h-6 rounded-lg bg-emerald-500 flex items-center justify-center">
                  <span className="text-white text-xs font-bold">AC</span>
                </div>
                <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">Actual</span>
              </div>
              <p className="text-sm font-bold text-gray-800 dark:text-white">
                {totalCosts?.actual_cost ? `CAD ${totalCosts?.actual_cost.toFixed(2)}` : "CAD 0.00"}
              </p>
            </div>
          </div>

          {/* View Details Button */}
          <button
            onClick={() => setShowDetails(true)}
            className="w-full py-2.5 px-3 bg-gray-50 dark:bg-surface-dark-3 rounded-lg flex items-center justify-between border border-gray-200 dark:border-gray-700 active:bg-gray-100 dark:active:bg-surface-dark-4 transition-colors"
          >
            <div className="flex items-center gap-2">
              <div
                className="w-5 h-5 bg-gray-500 dark:bg-gray-400"
                style={{
                  WebkitMaskImage: `url(${eyeIcon})`,
                  WebkitMaskRepeat: "no-repeat",
                  WebkitMaskPosition: "center",
                  WebkitMaskSize: "contain",
                  maskImage: `url(${eyeIcon})`,
                  maskRepeat: "no-repeat",
                  maskPosition: "center",
                  maskSize: "contain",
                }}
              />
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                View {all_breakdown.length} cost {all_breakdown.length === 1 ? 'entry' : 'entries'}
              </span>
            </div>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      {/* Second View - Details & Add Form */}
      {showDetails && (
        <div className="bg-white dark:bg-surface-dark min-h-full">
          {/* Sticky Header */}
          <div className="sticky top-0 z-10 bg-white dark:bg-surface-dark border-b border-gray-100 dark:border-gray-800 px-3 py-2.5">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowDetails(false)}
                className="w-9 h-9 rounded-full bg-gray-100 dark:bg-surface-dark-2 flex items-center justify-center active:bg-gray-200 dark:active:bg-surface-dark-3 transition-colors"
              >
                <div
                  className="w-5 h-5 bg-gray-600 dark:bg-gray-300"
                  style={{
                    WebkitMaskImage: `url(${BackIcon})`,
                    WebkitMaskRepeat: "no-repeat",
                    WebkitMaskPosition: "center",
                    WebkitMaskSize: "contain",
                    maskImage: `url(${BackIcon})`,
                    maskRepeat: "no-repeat",
                    maskPosition: "center",
                    maskSize: "contain",
                  }}
                />
              </button>
              <h2 className="text-base font-semibold text-gray-800 dark:text-white">Cost Details</h2>
            </div>
          </div>

          {/* Cost List */}
          <div className="">
            {all_breakdown.length > 0 ? (
              <div className="space-y-2 mb-4">
                {all_breakdown.map((cost, idx) => (
                  <div
                    key={idx}
                    className="bg-gray-50 dark:bg-surface-dark-2 rounded-xl p-3 border border-gray-200 dark:border-gray-800"
                  >
                    <div className="flex items-start justify-between mb-2 relative">
                      <h3 className="text-sm text-gray-600 font-bold dark:text-white flex-1 pr-2">
                        {cost.cost_title}
                      </h3>
                      {!isObserver() && <button
                        onClick={() => setOpenMenu((prev) => (prev === idx ? null : idx))}
                        className="w-7 h-7 rounded-full bg-white dark:bg-surface-dark-3 flex items-center justify-center shadow-sm active:bg-gray-100 dark:active:bg-surface-dark-4 transition-colors"
                      >
                        <div
                          className="w-3.5 h-3.5 bg-gray-500 dark:bg-gray-400"
                          style={{
                            WebkitMaskImage: `url(${MoreIcon})`,
                            WebkitMaskRepeat: "no-repeat",
                            WebkitMaskPosition: "center",
                            WebkitMaskSize: "contain",
                            maskImage: `url(${MoreIcon})`,
                            maskRepeat: "no-repeat",
                            maskPosition: "center",
                            maskSize: "contain",
                          }}
                        />
                      </button>}
                      {/* Remove Action Sheet - Rendered at card level */}
                      {openMenu === idx && (
                        <div
                          className="absolute right-2 top-6 z-[100] flex items-end justify-center bg-black/50 w-36"
                          onClick={() => setOpenMenu(null)}
                        >
                          <div
                            ref={removeOptionRef}
                            className="bg-white dark:bg-surface-dark-2 w-full max-w-sm rounded-xl overflow-hidden shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => handleRemoveClick(cost, idx)}
                              className="w-full py-2 text-red-500 font-medium text-sm active:bg-red-50 dark:active:bg-red-900/20 transition-colors"
                            >
                              Remove
                            </button>
                            <div className="h-1.5 bg-gray-100 dark:bg-gray-800" />
                            <button
                              onClick={() => setOpenMenu(null)}
                              className="w-full py-2 text-gray-700 dark:text-gray-300 font-medium text-sm active:bg-gray-50 dark:active:bg-surface-dark-3 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2">
                        <p className="text-xs text-blue-600 dark:text-blue-400 mb-0.5">Forecasted</p>
                        <p className="text-sm text-gray-600 font-bold dark:text-white">
                          CAD {Number(cost.forecasted_cost).toFixed(2)}
                        </p>
                      </div>
                      <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-2">
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 mb-0.5">Actual</p>
                        <p className="text-sm text-gray-600 font-bold dark:text-white">
                          CAD {Number(cost.actual_cost).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 mb-4">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-surface-dark-2 flex items-center justify-center">
                  <div
                    className="w-6 h-6 bg-gray-400 dark:bg-gray-500"
                    style={{
                      WebkitMaskImage: `url(${ExpanseIcon})`,
                      WebkitMaskRepeat: "no-repeat",
                      WebkitMaskPosition: "center",
                      WebkitMaskSize: "contain",
                      maskImage: `url(${ExpanseIcon})`,
                      maskRepeat: "no-repeat",
                      maskPosition: "center",
                      maskSize: "contain",
                    }}
                  />
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-xs">No cost entries yet</p>
              </div>
            )}

            {/* Add New Cost Form */}
            {!isObserver() && (
              <div className="bg-gray-50 dark:bg-surface-dark-2 rounded-xl p-3 border border-gray-200 dark:border-gray-800">
                <h3 className="text-sm text-gray-600 font-bold dark:text-white mb-3">Add New Cost</h3>

                {/* Cost Title Input */}
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                    Cost Title
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="cost_title"
                      placeholder="e.g., Materials, Labor, etc."
                      ref={input_ref}
                      value={mainCostBreakdown.cost_title}
                      onChange={handleChange}
                      className="w-full px-3 py-2.5 bg-white dark:bg-surface-dark-3 border border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
                    />
                    {mainCostBreakdown.cost_title && (
                      <button
                        onClick={() => setMainCostBreakdown(prev => ({ ...prev, cost_title: '' }))}
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-gray-100 dark:bg-surface-dark-4 flex items-center justify-center"
                      >
                        <div
                          className="w-3 h-3 bg-gray-500"
                          style={{
                            WebkitMaskImage: `url(${clearIcon})`,
                            WebkitMaskRepeat: "no-repeat",
                            WebkitMaskPosition: "center",
                            WebkitMaskSize: "contain",
                            maskImage: `url(${clearIcon})`,
                            maskRepeat: "no-repeat",
                            maskPosition: "center",
                            maskSize: "contain",
                          }}
                        />
                      </button>
                    )}
                  </div>
                </div>

                {/* Amount Inputs */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {/* Forecasted */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                      Forecasted
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="forecasted_cost"
                        placeholder="0.00"
                        value={mainCostBreakdown?.forecasted_cost}
                        onChange={handleChange}
                        className="w-full px-3 py-2.5 bg-white dark:bg-surface-dark-3 border border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all pr-8"
                      />
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                        <span className="text-blue-600 dark:text-blue-400 text-[8px] font-bold">$</span>
                      </div>
                    </div>
                  </div>

                  {/* Actual */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                      Actual
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="actual_cost"
                        placeholder="0.00"
                        value={mainCostBreakdown.actual_cost}
                        onChange={handleChange}
                        className="w-full px-3 py-2.5 bg-white dark:bg-surface-dark-3 border border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all pr-8"
                      />
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                        <span className="text-emerald-600 dark:text-emerald-400 text-[8px] font-bold">$</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Add Button */}
                <button
                  onClick={handleAddRow}
                  disabled={!mainCostBreakdown.cost_title?.trim() || (!mainCostBreakdown?.forecasted_cost?.trim() && !mainCostBreakdown?.actual_cost?.trim())}
                  className={`w-full py-3 rounded-lg font-semibold text-xs transition-all active:scale-[0.98]
                    ${!mainCostBreakdown.cost_title?.trim() || (!mainCostBreakdown?.forecasted_cost?.trim() && !mainCostBreakdown?.actual_cost?.trim())
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-teal-600 text-white shadow-md shadow-teal-500/25 active:bg-teal-600"}`}
                >
                  Add cost entry
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}